import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Input,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  FileTextOutlined,
  PlusOutlined,
  SettingOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';

const SYSTEM_BLOCK_LABELS: Record<string, string> = {
  patient_event_summary: '患者及事件摘要',
  calculation_summary: '主要计算结果',
  target_evaluation: '靶标达标评价',
  recommendation: '推荐方案',
  safety_evaluation: '安全性计算结果',
  selected_strategy: '选定补服策略',
  treatment_window_deviation: '治疗窗偏离结果',
  evidence_scope: '证据适用范围',
};

const PROFILE_SPECS = [
  { id: 'auc_exposure', name: 'AUC 暴露类', methods: ['Target AUC', 'AUC/MIC'] },
  { id: 'concentration_target', name: '浓度靶标类', methods: ['Target Concentration', 'Target Cmax', 'Cmax/MIC'] },
  { id: 'time_above_mic', name: '时间依赖类', methods: ['%fT>MIC'] },
  { id: 'remedial_dose', name: '补服/漏服类', methods: ['Remedial Dose'] },
];

export interface AIInterpretationBlock {
  id: string;
  name: string;
  content: string;
  enabled?: boolean;
  conditions?: Array<{ field: string; operator: string; value?: string | number | boolean }>;
}

export interface AIInterpretationProfile {
  id: string;
  name: string;
  methods: string[];
  enabled?: boolean;
  system_prompt?: string;
  template: Array<{
    block_type: 'system' | 'custom';
    block_id: string;
    enabled?: boolean;
    content_override?: string;
  }>;
}

export interface AIInterpretationConfig {
  version: 2;
  content_blocks: AIInterpretationBlock[];
  profiles: AIInterpretationProfile[];
}

const profileForMethod = (method: string) => PROFILE_SPECS.find((profile) => profile.methods.includes(method));

export const normalizeAIInterpretationConfig = (
  raw: any,
  methods: string[],
): AIInterpretationConfig => {
  if (raw?.version === 2) {
    const existingProfiles: AIInterpretationProfile[] = (Array.isArray(raw.profiles) ? raw.profiles : []).map((profile: any) => ({
      ...profile,
      methods: Array.isArray(profile?.methods) ? profile.methods : [],
      template: (Array.isArray(profile?.template) ? profile.template : []).map((entry: any) => ({
        block_type: entry?.block_type === 'system' ? 'system' : 'custom',
        block_id: String(entry?.block_id || ''),
        enabled: entry?.enabled !== false,
        content_override: String(entry?.content_override || ''),
      })).filter((entry: any) => entry.block_id),
    }));
    const assigned = new Set(existingProfiles.flatMap((profile) => profile.methods || []));
    const missingGroups = new Map<string, string[]>();
    methods.filter((method) => !assigned.has(method)).forEach((method) => {
      const spec = profileForMethod(method);
      const profileId = spec?.id || `custom_${method}`;
      missingGroups.set(profileId, [...(missingGroups.get(profileId) || []), method]);
    });
    const appended = Array.from(missingGroups.entries()).map(([profileId, profileMethods]) => {
      const spec = profileForMethod(profileMethods[0]);
      const alreadyExists = existingProfiles.find((profile) => profile.id === profileId);
      if (alreadyExists) {
        return { ...alreadyExists, methods: Array.from(new Set([...(alreadyExists.methods || []), ...profileMethods])) };
      }
      const systemBlocks = spec?.id === 'remedial_dose'
        ? ['patient_event_summary', 'selected_strategy', 'treatment_window_deviation', 'evidence_scope', 'safety_evaluation']
        : ['patient_event_summary', 'calculation_summary', 'target_evaluation', 'recommendation', 'safety_evaluation'];
      return {
        id: profileId,
        name: spec?.name || '其他方法',
        methods: profileMethods,
        enabled: true,
        system_prompt: '',
        template: systemBlocks.map((block_id) => ({ block_type: 'system' as const, block_id, enabled: true })),
      };
    });
    return {
      version: 2,
      content_blocks: Array.isArray(raw.content_blocks) ? raw.content_blocks : [],
      profiles: [...existingProfiles.filter((profile) => !appended.some((item) => item.id === profile.id)), ...appended],
    };
  }
  const legacyNames: Array<[string, string]> = [
    ['guideline_reference', '参考指南'],
    ['target_description', '靶标描述'],
    ['pkpd_mechanism', 'PK/PD机制说明'],
    ['safety_warnings', '安全性提醒'],
    ['monitoring_advice', '监测建议'],
  ];
  const blocks = legacyNames
    .filter(([id]) => String(raw?.[id] || '').trim())
    .map(([id, name]) => ({ id, name, content: String(raw[id]), enabled: true, conditions: [] }));
  const groupedMethods = new Map<string, string[]>();
  Array.from(new Set(methods)).forEach((method) => {
    const spec = profileForMethod(method);
    const profileId = spec?.id || `custom_${method}`;
    groupedMethods.set(profileId, [...(groupedMethods.get(profileId) || []), method]);
  });
  const profiles = Array.from(groupedMethods.entries()).map(([profileId, profileMethods]) => {
    const method = profileMethods[0];
    const spec = profileForMethod(method);
    return {
      id: profileId,
      name: spec?.name || '其他方法',
      methods: profileMethods,
      enabled: true,
      system_prompt: String(raw?.system_prompt || ''),
      template: [
        ...['pkpd_mechanism', 'target_description', 'guideline_reference']
          .filter((id) => blocks.some((block) => block.id === id))
          .map((block_id) => ({ block_type: 'custom' as const, block_id, enabled: true })),
        ...(spec?.id === 'remedial_dose'
          ? ['patient_event_summary', 'selected_strategy', 'treatment_window_deviation', 'evidence_scope', 'safety_evaluation']
          : ['patient_event_summary', 'calculation_summary', 'target_evaluation', 'recommendation', 'safety_evaluation'])
            .map((block_id) => ({ block_type: 'system' as const, block_id, enabled: true })),
        ...['safety_warnings', 'monitoring_advice']
          .filter((id) => blocks.some((block) => block.id === id))
          .map((block_id) => ({ block_type: 'custom' as const, block_id, enabled: true })),
      ],
    };
  });
  return { version: 2, content_blocks: blocks, profiles };
};

const newBlockId = () => `custom_block_${Date.now().toString(36)}`;

const CONDITION_PRESETS = [
  { value: 'always', label: '始终显示', conditions: [] },
  { value: 'target_not_met', label: '仅靶标未达标', conditions: [{ field: 'target_met', operator: 'equals', value: false }] },
  { value: 'has_safety_warning', label: '仅存在安全警告', conditions: [{ field: 'has_safety_warning', operator: 'equals', value: true }] },
  { value: 'remedial_only', label: '仅补服/漏服', conditions: [{ field: 'method', operator: 'equals', value: 'Remedial Dose' }] },
  { value: 'missed_only', label: '仅完全漏服', conditions: [{ field: 'event_classification', operator: 'equals', value: 'missed' }] },
];

const resolveConditionPreset = (block: AIInterpretationBlock) => (
  CONDITION_PRESETS.find((preset) => JSON.stringify(preset.conditions) === JSON.stringify(block.conditions || []))?.value || 'always'
);

const INTERPRETATION_PLACEHOLDERS = [
  '{{system_default}}',
  '{{drug_name}}',
  '{{diagnosis}}',
  '{{calculation_type}}',
  '{{primary_result}}',
  '{{target_range}}',
  '{{current_regimen}}',
  '{{recommended_regimen}}',
  '{{current_dose}}',
  '{{recommended_dose}}',
  '{{recommended_interval}}',
  '{{loading_regimen}}',
  '{{maintenance_regimen}}',
  '{{transition_time}}',
  '{{pta}}',
  '{{strategy_code}}',
  '{{strategy_interpretation}}',
  '{{time_above}}',
  '{{time_below}}',
  '{{total_deviation}}',
];

type ActiveEditor =
  | { type: 'system'; index: number }
  | { type: 'custom'; id: string }
  | null;

const appendPlaceholder = (content: string, placeholder: string) => {
  if (!content) return placeholder;
  return `${content}${content.endsWith(' ') || content.endsWith('\n') ? '' : ' '}${placeholder}`;
};

export const AIInterpretationProfileEditor: React.FC<{
  value?: AIInterpretationConfig;
  onChange?: (value: AIInterpretationConfig) => void;
  methods?: string[];
}> = ({ value, onChange, methods = [] }) => {
  const normalized = useMemo(() => normalizeAIInterpretationConfig(value, methods), [value, methods]);
  const externalSignature = useMemo(() => JSON.stringify(normalized), [normalized]);
  const appliedExternalSignature = useRef(externalSignature);
  const [config, setConfig] = useState<AIInterpretationConfig>(normalized);
  const [activeProfileId, setActiveProfileId] = useState<string>(normalized.profiles[0]?.id || '');
  const [activeEditor, setActiveEditor] = useState<ActiveEditor>(null);

  useEffect(() => {
    if (appliedExternalSignature.current === externalSignature) return;
    appliedExternalSignature.current = externalSignature;
    setConfig(normalized);
    setActiveProfileId((current) => (
      normalized.profiles.some((profile) => profile.id === current)
        ? current
        : normalized.profiles[0]?.id || ''
    ));
  }, [externalSignature, normalized]);

  const commit = (next: AIInterpretationConfig) => {
    setConfig(next);
    onChange?.(next);
  };
  const activeProfile = config.profiles.find((profile) => profile.id === activeProfileId) || config.profiles[0];

  const updateProfile = (updater: (profile: AIInterpretationProfile) => AIInterpretationProfile) => {
    if (!activeProfile) return;
    commit({
      ...config,
      profiles: config.profiles.map((profile) => profile.id === activeProfile.id ? updater(profile) : profile),
    });
  };

  const addBlock = () => {
    const block: AIInterpretationBlock = {
      id: newBlockId(),
      name: '自定义医学内容',
      content: '',
      enabled: true,
      conditions: [],
    };
    commit({ ...config, content_blocks: [...config.content_blocks, block] });
  };

  const updateBlock = (id: string, patch: Partial<AIInterpretationBlock>) => {
    commit({
      ...config,
      content_blocks: config.content_blocks.map((block) => block.id === id ? { ...block, ...patch } : block),
    });
  };

  const removeBlock = (id: string) => {
    commit({
      ...config,
      content_blocks: config.content_blocks.filter((block) => block.id !== id),
      profiles: config.profiles.map((profile) => ({
        ...profile,
        template: profile.template.filter((entry) => entry.block_id !== id),
      })),
    });
  };

  const addTemplateBlock = (blockType: 'system' | 'custom', blockId: string) => {
    if (!activeProfile || !blockId) return;
    if (activeProfile.template.some((entry) => entry.block_type === blockType && entry.block_id === blockId)) return;
    updateProfile((profile) => ({
      ...profile,
      template: [...profile.template, { block_type: blockType, block_id: blockId, enabled: true }],
    }));
  };

  const moveTemplate = (index: number, offset: number) => {
    updateProfile((profile) => {
      const nextIndex = index + offset;
      if (nextIndex < 0 || nextIndex >= profile.template.length) return profile;
      const template = [...profile.template];
      [template[index], template[nextIndex]] = [template[nextIndex], template[index]];
      return { ...profile, template };
    });
  };

  const insertPlaceholder = (placeholder: string) => {
    if (!activeEditor) return;
    if (activeEditor.type === 'custom') {
      const block = config.content_blocks.find((item) => item.id === activeEditor.id);
      if (block) updateBlock(block.id, { content: appendPlaceholder(block.content, placeholder) });
      return;
    }
    updateProfile((profile) => ({
      ...profile,
      template: profile.template.map((entry, index) => index === activeEditor.index
        ? { ...entry, content_override: appendPlaceholder(entry.content_override || '', placeholder) }
        : entry),
    }));
  };

  if (!activeProfile) {
    return (
      <Alert
        type="info"
        showIcon
        message="尚未关联计算方法"
        description="请先在药物配置的计算方法中启用方法，再为其配置解读档案。"
      />
    );
  }

  const templateRows = activeProfile.template.map((entry, index) => ({
    ...entry,
    key: `${entry.block_type}-${entry.block_id}-${index}`,
    label: entry.block_type === 'system'
      ? SYSTEM_BLOCK_LABELS[entry.block_id] || entry.block_id
      : config.content_blocks.find((block) => block.id === entry.block_id)?.name || entry.block_id,
    index,
  }));

  return (
    <div className="admin-ai-interpretation-editor">
      <aside className="admin-ai-interpretation-editor__sidebar">
        <div className="admin-ai-interpretation-editor__sidebar-title">
          <FileTextOutlined />
          <span>解读档案</span>
        </div>
        <div className="admin-ai-interpretation-editor__profile-list">
          {config.profiles.map((profile) => (
            <button
              key={profile.id}
              type="button"
              className={`admin-ai-interpretation-editor__profile${profile.id === activeProfile.id ? ' is-active' : ''}`}
              onClick={() => { setActiveProfileId(profile.id); setActiveEditor(null); }}
            >
              <strong>{profile.name}</strong>
              <span>{profile.methods.join(' · ') || '未关联方法'}</span>
            </button>
          ))}
        </div>
        <Tag className="admin-ai-interpretation-editor__version">配置版本 v2</Tag>
      </aside>

      <main className="admin-ai-interpretation-editor__canvas">
        <header className="admin-ai-interpretation-editor__profile-header">
          <div>
            <Typography.Title level={5}>{activeProfile.name}</Typography.Title>
            <Typography.Text type="secondary">{activeProfile.methods.join('、') || '未关联计算方法'}</Typography.Text>
          </div>
          <Space size={8}>
            <Typography.Text type="secondary">启用档案</Typography.Text>
            <Switch
              checked={activeProfile.enabled !== false}
              onChange={(checked) => updateProfile((profile) => ({ ...profile, enabled: checked }))}
            />
          </Space>
        </header>

        <section className="admin-ai-interpretation-editor__section">
          <div className="admin-ai-interpretation-editor__section-title">
            <SettingOutlined />
            <span>档案设置</span>
          </div>
          <div className="admin-ai-interpretation-editor__settings-grid">
            <label>
              <span>档案名称</span>
              <Input
                value={activeProfile.name}
                onChange={(event) => updateProfile((profile) => ({ ...profile, name: event.target.value }))}
              />
            </label>
            <label>
              <span>关联计算方法</span>
              <Select
                mode="multiple"
                value={activeProfile.methods}
                style={{ width: '100%' }}
                options={methods.map((method) => ({ value: method, label: method }))}
                onChange={(selected) => updateProfile((profile) => ({ ...profile, methods: selected }))}
              />
            </label>
          </div>
          <label className="admin-ai-interpretation-editor__field">
            <span>档案系统提示词</span>
            <Input.TextArea
              value={activeProfile.system_prompt}
              rows={3}
              placeholder="可选"
              onChange={(event) => updateProfile((profile) => ({ ...profile, system_prompt: event.target.value }))}
            />
          </label>
        </section>

        <section className="admin-ai-interpretation-editor__section admin-ai-interpretation-editor__variables">
          <div className="admin-ai-interpretation-editor__section-title">
            <span>可插入变量</span>
            <Typography.Text type="secondary">{activeEditor ? '点击变量插入当前编辑框' : '请先定位一个内容编辑框'}</Typography.Text>
          </div>
          <div className="admin-ai-interpretation-editor__token-list">
            {INTERPRETATION_PLACEHOLDERS.map((placeholder) => (
              <Button
                key={placeholder}
                size="small"
                disabled={!activeEditor}
                onClick={() => insertPlaceholder(placeholder)}
              >
                {placeholder}
              </Button>
            ))}
          </div>
        </section>

        <section className="admin-ai-interpretation-editor__section">
          <div className="admin-ai-interpretation-editor__section-title admin-ai-interpretation-editor__section-title--actions">
            <span><UnorderedListOutlined /> 报告内容与顺序</span>
            <Space wrap>
              <Select
                size="small"
                placeholder="添加系统内容"
                style={{ width: 170 }}
                options={Object.entries(SYSTEM_BLOCK_LABELS).map(([value, label]) => ({ value, label }))}
                onChange={(value) => { if (value) addTemplateBlock('system', value); }}
                value={undefined}
              />
              <Select
                size="small"
                placeholder="添加自定义内容"
                style={{ width: 170 }}
                options={config.content_blocks.map((block) => ({ value: block.id, label: block.name }))}
                onChange={(value) => { if (value) addTemplateBlock('custom', value); }}
                value={undefined}
              />
            </Space>
          </div>
          <div className="admin-ai-interpretation-editor__template-list">
            {templateRows.map((row) => (
              <div className="admin-ai-interpretation-editor__template-row" key={row.key}>
                <div className="admin-ai-interpretation-editor__template-head">
                  <div className="admin-ai-interpretation-editor__template-name">
                    <span className="admin-ai-interpretation-editor__order">{row.index + 1}</span>
                    <Tag color={row.block_type === 'system' ? 'blue' : 'default'}>{row.block_type === 'system' ? '系统' : '自定义'}</Tag>
                    <strong>{row.label}</strong>
                  </div>
                  <Space size={2}>
                    <Switch
                      size="small"
                      checked={row.enabled !== false}
                      onChange={(checked) => updateProfile((profile) => ({
                        ...profile,
                        template: profile.template.map((entry, index) => index === row.index ? { ...entry, enabled: checked } : entry),
                      }))}
                    />
                    <Tooltip title="上移"><Button type="text" icon={<ArrowUpOutlined />} disabled={row.index === 0} onClick={() => moveTemplate(row.index, -1)} /></Tooltip>
                    <Tooltip title="下移"><Button type="text" icon={<ArrowDownOutlined />} disabled={row.index === templateRows.length - 1} onClick={() => moveTemplate(row.index, 1)} /></Tooltip>
                    <Tooltip title="移除"><Button type="text" danger icon={<DeleteOutlined />} onClick={() => updateProfile((profile) => ({ ...profile, template: profile.template.filter((_, index) => index !== row.index) }))} /></Tooltip>
                  </Space>
                </div>
                {row.block_type === 'system' ? (
                  <>
                    <Input.TextArea
                      aria-label={`${row.label}内容模板`}
                      value={row.content_override || ''}
                      rows={4}
                      placeholder="留空使用系统预设文本；也可输入自定义模板，并用 {{primary_result}} 等变量插入动态结果"
                      onFocus={() => setActiveEditor({ type: 'system', index: row.index })}
                      onChange={(event) => updateProfile((profile) => ({
                        ...profile,
                        template: profile.template.map((entry, index) => index === row.index
                          ? { ...entry, content_override: event.target.value }
                          : entry),
                        }))}
                    />
                    <Typography.Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                      留空时保留系统根据病例动态生成的默认内容；填写后将替换该模块的默认表达。
                    </Typography.Text>
                  </>
                ) : (
                  <Typography.Text type="secondary">
                    {config.content_blocks.find((block) => block.id === row.block_id)?.content || '内容尚未填写'}
                  </Typography.Text>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="admin-ai-interpretation-editor__section" id="admin-ai-custom-content-library">
          <div className="admin-ai-interpretation-editor__section-title admin-ai-interpretation-editor__section-title--actions">
            <span>自定义医学内容</span>
            <Button
              aria-label="添加自定义内容块"
              icon={<PlusOutlined />}
              onClick={addBlock}
            >
              添加内容块
            </Button>
          </div>
          {config.content_blocks.length === 0 ? (
            <div className="admin-ai-interpretation-editor__empty">尚无自定义医学内容</div>
          ) : (
            <div className="admin-ai-interpretation-editor__custom-list">
              {config.content_blocks.map((block) => (
                <div className="admin-ai-interpretation-editor__custom-block" key={block.id}>
                  <div className="admin-ai-interpretation-editor__custom-toolbar">
                    <Input
                      aria-label="自定义内容名称"
                      value={block.name}
                      onChange={(event) => updateBlock(block.id, { name: event.target.value })}
                    />
                    <Select
                      aria-label={`${block.name}显示条件`}
                      value={resolveConditionPreset(block)}
                      options={CONDITION_PRESETS.map(({ value, label }) => ({ value, label }))}
                      onChange={(value) => updateBlock(block.id, {
                        conditions: CONDITION_PRESETS.find((preset) => preset.value === value)?.conditions || [],
                      })}
                    />
                    <Switch
                      checked={block.enabled !== false}
                      onChange={(checked) => updateBlock(block.id, { enabled: checked })}
                    />
                    <Tooltip title="删除内容块"><Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeBlock(block.id)} /></Tooltip>
                  </div>
                  <Input.TextArea
                    aria-label={`${block.name}内容模板`}
                    value={block.content}
                    rows={6}
                    onFocus={() => setActiveEditor({ type: 'custom', id: block.id })}
                    onChange={(event) => updateBlock(block.id, { content: event.target.value })}
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default AIInterpretationProfileEditor;
